import React, {
  Fragment,
  useEffect,
  useMemo,
  useState,
  useCallback,
} from "react";
import { useLocation } from "react-router-dom";
import { useDispatch } from "react-redux";
import SegmentMenu from "../../../../components/navegation/SegmentMenu";
import {
  clearSegments,
  setSegments,
} from "../../../../store/redux_auth/authSlice";
import LoadingToRealTransitionWrapper from "../../../../components/ui/transitions/LoadingToRealTransitionWraper";
import GhostCardContainer from "../../../../components/ui/loading/skeletons/GhostCardContainer";
import GhostDataTable from "../../../../components/ui/loading/skeletons/GhostDataTable";
import DataTable from "../../../../components/ui/table/DataTable";
import FilterModal from "../../../../components/ui/popup/FilterModal";
import EmptyState from "../../../../components/ui/pages/EmptyState";
import { useRefreshOnAuth } from "../../../../hooks/useRefreshOnAuth";
import { getLanguageContent } from "../../../../utils/language/languageSelector";
import { FILTER_SCHEMA } from "../../../../utils/schemas/language/components/filterSchema";
import {
  getSubscriptions,
  updateSubscriptionStatus,
} from "../../../../api/payments/subscriptionsAPI";
import { SUBSCRIPTIONS_SCHEMA } from "../../../../utils/schemas/language/pages/settings/subscriptionsSchema";
import SubscriptionDetailsModal from "../../../../components/ui/popup/settings/subscriptions/SubscriptionDetailsModal";
import CancelSubscriptionModal from "../../../../components/ui/popup/settings/subscriptions/CancelUnsubscribeModal";

const Subscriptions = () => {
  const location = useLocation();
  const dispatch = useDispatch();

  // Extract clean localization translation frames
  const content = getLanguageContent(SUBSCRIPTIONS_SCHEMA);
  const filterContent = getLanguageContent(FILTER_SCHEMA);

  const [isMounting, setIsMounting] = useState(true);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [subscriptions, setSubscriptions] = useState([]);
  const [filteredSubscriptions, setFilteredSubscriptions] = useState([]);

  const [currentPage, setCurrentPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [isFilterOpen, setIsFilterOpen] = useState(false);

  const [serviceSearch, setServiceSearch] = useState("");
  const [selectedSubscription, setSelectedSubscription] = useState(null);

  const [cancelSubscriptionTarget, setCancelSubscriptionTarget] =
    useState(null);
  const [isCancellingSubmit, setIsCancellingSubmit] = useState(false);

  const [filters, setFilters] = useState({
    date: "",
    status: "",
  });

  const [draftFilters, setDraftFilters] = useState({
    date: "",
    status: "",
  });

  const subscriptionSegments = useMemo(() => {
    return [
      {
        path: "/account/subscriptions",
        text: content.navigation.segment_text,
        normalIcon: `${process.env.PUBLIC_URL}/images/icons/payout_grey.svg`,
        activeIcon: `${process.env.PUBLIC_URL}/images/icons/payout_blue.svg`,
      },
    ];
  }, [content.navigation.segment_text]);

  // Strict headers extraction from schema dictionaries
  const headers = useMemo(
    () => [
      {
        label: content.table.headers.service,
        key: "service",
        sortable: false,
        width: "240px",
      },
      {
        label: content.table.headers.status,
        key: "status",
        sortable: false,
        type: "subscription_status",
        width: "240px",
      },
      {
        label: content.table.headers.period,
        key: "period",
        sortable: false,
        width: "240px",
      },
    ],
    [content.table.headers],
  );

  // ============================================================
  // DATA INTERFACE PARSING & TRANSFORM LOGIC
  // ============================================================
  const transformSubscription = useCallback(
    (sub) => {
      const rawStatus = sub.status ? sub.status.toUpperCase() : "";
      let formattedStatus = "DRA"; // Fallback por texto si no existiera en el schema

      if (rawStatus === "ACT") {
        formattedStatus = content.table.statuses.active; // "Active"
      } else if (rawStatus === "CAN") {
        formattedStatus = content.table.statuses.cancelled; // "Cancelled"
      } else if (rawStatus === "CLL") {
        formattedStatus = "Non-Renewing"; // Período de gracia (Ámbar)
      } else if (rawStatus === "DUE") {
        formattedStatus = "Due"; // Fallo de pago
      } else if (rawStatus === "DRA") {
        formattedStatus = content.table.statuses.draft; // "Draft"
      }

      let displayPeriod = content.table.periods.monthly;
      if (sub.payment_interval) {
        const intervalUpper = sub.payment_interval.toUpperCase();
        if (intervalUpper === "YEARLY" || intervalUpper === "YRL") {
          displayPeriod = content.table.periods.yearly;
        } else if (intervalUpper === "QUARTERLY" || intervalUpper === "QTR") {
          displayPeriod = content.table.periods.quarterly;
        }
      }

      return {
        id: sub.uuid,
        service:
          sub.feature_name || sub.plan_name || content.table.fallback_service,
        status: formattedStatus,
        period: displayPeriod,
        raw_payload: sub,
      };
    },
    [content.table],
  );

  // ============================================================
  // BACKEND API LIFECYCLE
  // ============================================================
  const loadSubscriptions = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await getSubscriptions();
      if (response.status === 401) return;

      const data = await response.json();

      if (response.status === 200) {
        const subList = data.subscriptions || data || [];
        const transformed = subList.map(transformSubscription);

        setSubscriptions(transformed);
        setHasMore(data.has_more || data.hasMore || false);
      } else {
        setError(data.message || content.errors.fetch_failed);
        setSubscriptions([]);
      }
    } catch (err) {
      console.error("Error fetching subscriptions:", err);
      setError(content.errors.general_fallback);
      setSubscriptions([]);
    } finally {
      setLoading(false);
    }
  }, [transformSubscription, content.errors]);

  useRefreshOnAuth(loadSubscriptions);

  useEffect(() => {
    loadSubscriptions();
  }, [loadSubscriptions, currentPage]);

  // Client side filtering pipeline
  useEffect(() => {
    let filtered = subscriptions;

    if (filters.status) {
      if (filters.status === "Active") {
        filtered = filtered.filter(
          (sub) => sub.status === "Active" || sub.status === "Non-Renewing",
        );
      } else {
        filtered = filtered.filter(
          (sub) => sub.status.toLowerCase() === filters.status.toLowerCase(),
        );
      }
    }

    setFilteredSubscriptions(filtered);
  }, [subscriptions, filters]);

  useEffect(() => {
    const hasActiveFilters = Object.values(filters).some((val) => val !== "");
    if (hasActiveFilters && currentPage !== 1) {
      setCurrentPage(1);
    }
  }, [filters]);

  useEffect(() => {
    const timer = setTimeout(() => setIsMounting(false), 300);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    dispatch(setSegments(subscriptionSegments));
    return () => dispatch(clearSegments());
  }, [dispatch, subscriptionSegments]);

  // ============================================================
  // UNSUBSCRIBE BACKEND PIPELINE SUBMIT
  // ============================================================
  const handleConfirmCancelSubscription = async (payload) => {
    try {
      setIsCancellingSubmit(true);

      const requestBody = {
        action: "unsubscribe",
        keep_institution_uuid: payload.keepInstitution
          ? payload.keepInstitution.uuid
          : null,
        keep_credit_rule_uuid: payload.keepCreditRule
          ? payload.keepCreditRule.uuid
          : null,
      };

      const response = await updateSubscriptionStatus(
        payload.subscriptionId,
        requestBody,
      );
      if (response.status === 401) return;

      if (response.ok) {
        setCancelSubscriptionTarget(null);
        loadSubscriptions();
      } else {
        const errData = await response.json();
        alert(
          errData?.detail || "Failed to cancel subscription. Please try again.",
        );
      }
    } catch (err) {
      console.error("Error in unsubscribe transaction:", err);
      alert("Something went wrong. Please try again.");
    } finally {
      setIsCancellingSubmit(false);
    }
  };

  const getSubscriptionActions = (row) => {
    const actions = [
      {
        label: content.table.actions.view_details,
        onClick: () => setSelectedSubscription(row),
      },
    ];

    // Show unsubscribe only if status is Active or Non-Renewing
    if (row.status === content.table.statuses.active) {
      actions.push({
        label: content.table.actions.unsubscribe,
        onClick: () => {
          setCancelSubscriptionTarget(row);
        },
      });
    }

    return actions;
  };

  const searchRightElement = (
    <div
      className="search-input-element"
      style={{ position: "relative", display: "flex", alignItems: "center" }}
    >
      <div className="form-field" style={{ flex: 1 }}>
        <input
          className="form-input"
          type="text"
          placeholder={content.table.search_placeholder}
          value={serviceSearch}
          onChange={(event) => setServiceSearch(event.target.value)}
          style={{ paddingRight: "40px" }}
        />
      </div>
      <img
        src={`${process.env.PUBLIC_URL}/images/icons/search_blue.svg`}
        alt=""
        style={{
          position: "absolute",
          right: "10px",
          width: "22px",
          pointerEvents: "none",
        }}
      />
    </div>
  );

  const quickStatusFilterElement = (
    <div className="utc-header-filtering transactions-date-filter">
      {[
        { label: content.table.quick_filters.all, value: "" },
        { label: content.table.quick_filters.active, value: "Active" },
        { label: content.table.quick_filters.cancelled, value: "Cancelled" },
      ].map((item) => (
        <div
          key={item.label}
          className={`utc-header-filtering-item ${
            filters.status === item.value ? "active" : ""
          }`}
          onClick={() =>
            setFilters((prev) => ({ ...prev, status: item.value }))
          }
          style={{ cursor: "pointer" }}
        >
          {item.label}
        </div>
      ))}
    </div>
  );

  const totalPagesCalculation = useMemo(() => {
    return hasMore || currentPage > 1
      ? currentPage + (hasMore ? 1 : 0)
      : currentPage;
  }, [hasMore, currentPage]);

  return (
    <div className="main-content-area">
      <div className="content">
        <div className="ich-wrapper">
          <div className="internal-content-header">
            <SegmentMenu
              segments={subscriptionSegments}
              currentPath={location.pathname + location.search}
              loading={isMounting}
            />
          </div>
        </div>

        <Fragment>
          <div className="internal-content-body">
            {error ? (
              <EmptyState
                title={content.errors.unable_to_load_title}
                text={error}
              />
            ) : (
              <LoadingToRealTransitionWrapper
                isLoading={loading}
                ghost={
                  <GhostCardContainer>
                    <GhostDataTable
                      headers={headers}
                      rows={4}
                      title={true}
                      description={false}
                      showSearch={false}
                    />
                  </GhostCardContainer>
                }
              >
                <DataTable
                  title={content.table.title}
                  headerMiddleElement={quickStatusFilterElement}
                  headers={headers}
                  rows={filteredSubscriptions}
                  getActionMenu={getSubscriptionActions}
                  filterElement={true}
                  onFilterClick={() => {
                    setDraftFilters(filters);
                    setIsFilterOpen(true);
                  }}
                  currentPage={currentPage}
                  totalPages={totalPagesCalculation}
                  onPageChange={(page) => setCurrentPage(page)}
                  emptyStateTitle={content.empty_state.title}
                  emptyStateText={content.empty_state.text}
                />
              </LoadingToRealTransitionWrapper>
            )}
          </div>
        </Fragment>
      </div>

      <FilterModal
        isOpen={isFilterOpen}
        onClose={() => setIsFilterOpen(false)}
        onApply={() => {
          setFilters(draftFilters);
          setIsFilterOpen(false);
        }}
        title={content.table.filter_title}
        applyText={filterContent?.apply || "Apply"}
      >
        <div className="filter-field">
          <select
            className="form-input"
            value={draftFilters.status}
            onChange={(e) =>
              setDraftFilters((prev) => ({ ...prev, status: e.target.value }))
            }
          >
            <option value="">
              {filterContent?.all_statuses || "All Statuses"}
            </option>
            <option value="Active">{content.table.statuses.active}</option>
            <option value="Cancelled">
              {content.table.statuses.cancelled}
            </option>
          </select>
        </div>
      </FilterModal>
      <SubscriptionDetailsModal
        isOpen={Boolean(selectedSubscription)}
        onClose={() => setSelectedSubscription(null)}
        subscription={selectedSubscription}
        copy={content.details}
      />
      <CancelSubscriptionModal
        isOpen={Boolean(cancelSubscriptionTarget)}
        onClose={() => setCancelSubscriptionTarget(null)}
        subscription={cancelSubscriptionTarget}
        onConfirm={handleConfirmCancelSubscription}
        isSubmitting={isCancellingSubmit}
      />
    </div>
  );
};

export default Subscriptions;
